/**
 * ProgressiveImage — chargement progressif avec 2 modes :
 *
 * 1. Blur-up : si `lowSrc` fourni (typiquement le thumb 17 KB), affiche cette image
 *    en background flouté pendant que le `src` haute déf charge, puis crossfade.
 *
 * 2. Shimmer : si pas de `lowSrc`, affiche un placeholder shimmer animé.
 *
 * Utilisation :
 *   <ProgressiveImage
 *     src={getMediaUrl(photo, 'medium', token)}
 *     lowSrc={getMediaUrl(photo, 'thumb', token)}
 *     srcSet={getMediaSrcSet(photo, token)}
 *     sizes="100vw"
 *     alt="Photo bien"
 *     className="w-full h-full object-cover"
 *   />
 */
import React, { useState } from 'react';

const ProgressiveImage = ({
  src,
  lowSrc,
  srcSet,
  sizes,
  alt = '',
  className = '',
  containerClassName = '',
  onClick,
  testId,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div
      className={`relative overflow-hidden ${containerClassName}`}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      {/* Placeholder : blur-up si lowSrc dispo, sinon shimmer */}
      {!loaded && !error && (
        lowSrc ? (
          <img
            src={lowSrc}
            alt=""
            aria-hidden="true"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${className}`}
            style={{ filter: 'blur(20px)', transform: 'scale(1.08)' }}
          />
        ) : (
          <div
            className="absolute inset-0 w-full h-full"
            style={{
              background: 'linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s ease-in-out infinite',
            }}
            aria-hidden="true"
          />
        )
      )}

      {/* Image full quality */}
      {!error && (
        <img
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          data-testid={testId}
          className={`relative transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`}
        />
      )}

      {/* Fallback erreur de chargement */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100 text-slate-300">
          <i className="bi bi-image text-3xl" aria-hidden="true"></i>
        </div>
      )}

      {/* Keyframes inline (au cas où le projet ne définit pas shimmer ailleurs) */}
      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export default ProgressiveImage;
